<template>
    <component :is="page ? 'div' : 'a-modal'" v-bind="page ? {} : modalProps" @cancel="$emit('close')">
        <div class="pfm-modal" :class="{ 'pfm-page': page }">
            <div class="pfm-head" v-if="!page">
                <div>
                    <h3>{{ isEdit ? "Editar produto" : "Novo produto" }}</h3>
                    <div class="pfm-sub">{{ isEdit ? product.pro_descricao : "Cadastre um novo produto no seu catálogo" }}</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="pfm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="pfm-body">
                <cc-loader v-if="loading" style="margin: 10vh 0" />
                <template v-else>
                    <div v-if="serverMsg" class="pfm-notice">
                        <span class="material-icons-outlined">error_outline</span>
                        {{ serverMsg }}
                    </div>

                    <a-form v-show="showData" layout="vertical">
                        <a-row :gutter="16">
                            <a-col :xs="24" :md="12">
                                <a-form-item label="Descrição" required :validate-status="errors.description ? 'error' : ''">
                                    <a-input v-model="form.description" placeholder="Descrição do produto" :disabled="dataLocked" />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="12">
                                <a-form-item label="Categoria" required :validate-status="errors.category ? 'error' : ''">
                                    <a-select
                                        v-model="form.categoryId"
                                        show-search
                                        :disabled="dataLocked"
                                        placeholder="Categoria"
                                        :options="categoryOptions"
                                        :filterOption="filterOption"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="6">
                                <a-form-item label="Código interno">
                                    <a-input v-model="form.internalCode" placeholder="Código interno" :disabled="dataLocked" />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="6">
                                <a-form-item label="Código" required :validate-status="errors.ean ? 'error' : ''">
                                    <a-input :value="form.ean" placeholder="Código" :disabled="dataLocked || eanLocked" @input="onEanInput" />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="6">
                                <a-form-item label="Unidade" required :validate-status="errors.wrap ? 'error' : ''">
                                    <a-select
                                        v-if="!form.isGeneric"
                                        v-model="form.wrapCode"
                                        show-search
                                        placeholder="Unidade"
                                        :disabled="dataLocked"
                                        :options="wrapOptions"
                                        :filterOption="filterOption"
                                        @change="onWrapChange"
                                    />
                                    <div v-else class="pfm-inline">
                                        <a-input v-if="editingGenericUnit" v-model="form.genericUnit" placeholder="Digite a unidade" :disabled="dataLocked" />
                                        <a-select
                                            v-else
                                            v-model="form.genericUnit"
                                            show-search
                                            placeholder="Unidade"
                                            :disabled="dataLocked"
                                            :options="genericUnitOptions"
                                            :filterOption="filterOption"
                                        />
                                        <a-tooltip :title="editingGenericUnit ? 'Escolher da lista' : 'Digitar unidade'">
                                            <a-button class="pfm-icon-btn" :disabled="dataLocked" @click="editingGenericUnit = !editingGenericUnit">
                                                <span class="material-icons-outlined">{{ editingGenericUnit ? "list" : "edit" }}</span>
                                            </a-button>
                                        </a-tooltip>
                                    </div>
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="6">
                                <a-form-item label="Qtd. embalagem" required :validate-status="errors.packQty ? 'error' : ''">
                                    <a-input :value="form.packQty" :disabled="dataLocked || packQtyLocked" placeholder="Qtd." @input="onPackQtyInput" />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="12">
                                <a-form-item label="Fornecedor exclusivo">
                                    <a-select
                                        :value="form.providerId"
                                        show-search
                                        allowClear
                                        placeholder="Nenhum"
                                        :disabled="dataLocked"
                                        :options="providerOptions"
                                        :filterOption="filterOption"
                                        @change="onProviderChange"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :md="12">
                                <a-form-item label="Vendedor" :validate-status="errors.seller ? 'error' : ''" :help="errors.seller ? 'Selecione o vendedor do fornecedor exclusivo.' : ''">
                                    <a-select
                                        v-model="form.sellerId"
                                        show-search
                                        allowClear
                                        placeholder="Vendedor"
                                        :disabled="dataLocked || !form.providerId"
                                        :loading="loadingSellers"
                                        :options="sellerOptions"
                                        :filterOption="filterOption"
                                    />
                                </a-form-item>
                            </a-col>
                        </a-row>

                        <div class="pfm-flags">
                            <a-checkbox :checked="form.isGeneric" :disabled="isEdit" @change="(e) => toggleGeneric(e.target.checked)">Produto genérico</a-checkbox>
                            <a-checkbox v-model="form.directOrder" :disabled="dataLocked">Produto pedido direto</a-checkbox>
                            <a-radio-group v-model="form.active" button-style="solid" size="small" :disabled="dataLocked">
                                <a-radio-button :value="true">Ativo</a-radio-button>
                                <a-radio-button :value="false">Inativo</a-radio-button>
                            </a-radio-group>
                        </div>
                    </a-form>

                    <div v-if="!section" class="pfm-section-title">Sugestão por loja</div>
                    <a-table
                        v-show="showStores"
                        size="small"
                        :columns="storeColumns"
                        :data-source="stores"
                        row-key="loj_id"
                        :pagination="{ pageSize: 10, size: 'small' }"
                        :locale="{ emptyText: 'Nenhuma loja encontrada.' }"
                    >
                        <template slot="store" slot-scope="text">{{ String(text || "").toUpperCase() }}</template>
                        <template slot="suggestion" slot-scope="text, store">
                            <a-input
                                size="small"
                                class="pfm-qty"
                                :value="store.qtd_sugestao"
                                :disabled="dataLocked"
                                placeholder="0"
                                @input="(e) => onSuggestionInput(e, store)"
                            />
                        </template>
                    </a-table>
                </template>
            </div>

            <div class="pfm-foot">
                <a-button v-if="isEdit && form.isGeneric && !page" class="pfm-foot-left" @click="showBrands = true">
                    <i class="fa-solid fa-tags pfm-btn-icon"></i> Marcas
                </a-button>
                <a-button v-if="!page" :disabled="saving" @click="$emit('close')">Cancelar</a-button>
                <a-button
                    v-if="page && isEdit"
                    class="pfm-unlock-btn"
                    :type="dataUnlocked ? 'primary' : 'default'"
                    :ghost="dataUnlocked"
                    :disabled="saving"
                    @click="dataUnlocked = !dataUnlocked"
                >
                    <span class="material-icons-outlined">edit</span>
                    Editar
                </a-button>
                <a-button type="primary" :loading="saving" :disabled="loading || dataLocked" @click="submit">Salvar</a-button>
            </div>
        </div>

        <ProductBrandsV2Modal v-if="showBrands" :product="product" @close="showBrands = false" />
    </component>
</template>

<script>
import ProductsV3Service from "@/services/v3/products/product.service";
import ProviderService from "@/services/v3/provider.service";
import SellersService from "@/services/v3/commons/sellers.service";
import StoreService from "@/services/v3/stores/store.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ProductBrandsV2Modal from "./product-brands-v2.modal.vue";

const WRAPS = [
    { name: "Atado", value: "AT" },
    { name: "Unidade", value: "UN" },
    { name: "Caixa", value: "CX" },
    { name: "Fardo", value: "FD" },
    { name: "Kilograma", value: "KG" },
    { name: "Duzia", value: "DZ" },
    { name: "Display", value: "DP" },
    { name: "Pacote", value: "PC" },
];

export default {
    name: "ProductFormV2Modal",
    components: { ProductBrandsV2Modal },
    props: {
        product: { type: Object, default: () => ({}) },
        page: { type: Boolean, default: false },
        section: { type: String, default: null },
    },
    data() {
        const p = this.product || {};
        const packing = String(p.pro_embalagem || "").split(" ");
        const isGeneric = p.pro_generico === 1 || p.pro_generico === "1";
        return {
            store_service: new StoreService(),
            products_v3_service: new ProductsV3Service(),
            provider_service: new ProviderService(),
            sellers_service: new SellersService(),
            loading: true,
            saving: false,
            loadingSellers: false,
            serverMsg: "",
            showBrands: false,
            dataUnlocked: false,
            editingGenericUnit: false,
            categories: [],
            providers: [],
            sellers: [],
            stores: [],
            errors: {},
            form: {
                description: p.pro_descricao || "",
                categoryId: p.cat_id || undefined,
                internalCode: p.pro_codInterno || "",
                ean: p.pro_ean || "",
                isGeneric,
                directOrder: !!Number(p.pro_pedido_direto || 0),
                active: p.pro_id ? parseInt(p.pro_status) === 1 : true,
                wrapCode: !isGeneric && packing[0] ? packing[0] : undefined,
                genericUnit: isGeneric ? p.pro_unidade_compra : undefined,
                packQty: p.pro_id ? packing[1] || "" : "1",
                providerId: undefined,
                sellerId: p.pro_vend_id || undefined,
            },
            storeColumns: [
                { title: "ID", dataIndex: "loj_id", key: "loj_id", width: 80, align: "center" },
                { title: "Empresa", dataIndex: "loj_nomeFantasia", key: "loj_nomeFantasia", scopedSlots: { customRender: "store" } },
                { title: "Qtd. sugestão", key: "suggestion", align: "center", width: 140, scopedSlots: { customRender: "suggestion" } },
            ],
        };
    },
    computed: {
        modalProps() {
            return {
                visible: true,
                width: "70vw",
                footer: null,
                closable: false,
                destroyOnClose: true,
                maskClosable: false,
                centered: true,
                wrapClassName: "pfm-wrap",
            };
        },
        isEdit() {
            return !!this.product.pro_id;
        },
        dataLocked() {
            return this.saving || (this.page && this.isEdit && !this.dataUnlocked);
        },
        showData() {
            return !this.section || this.section === "DATA";
        },
        showStores() {
            return !this.section || this.section === "STORES";
        },
        eanLocked() {
            return this.product.pro_generico == 1;
        },
        packQtyLocked() {
            return this.form.isGeneric || this.form.wrapCode === "UN" || this.form.wrapCode === "DZ";
        },
        wrapOptions() {
            return WRAPS.map((w) => ({ value: w.value, label: w.name }));
        },
        genericUnitOptions() {
            const names = WRAPS.map((w) => w.name);
            if (this.form.genericUnit && !names.includes(this.form.genericUnit)) names.push(this.form.genericUnit);
            return names.map((name) => ({ value: name, label: name }));
        },
        categoryOptions() {
            return this.categories.map((c) => ({ value: c.cat_id, label: c.cat_descricao }));
        },
        providerOptions() {
            return this.providers.map((p) => ({ value: p.id, label: p.name }));
        },
        sellerOptions() {
            const options = this.sellers.map((s) => ({ value: s.id, label: s.name }));
            const current = this.form.sellerId;
            if (current && !options.some((o) => o.value == current) && this.product.pro_vend_id == current) {
                options.push({ value: current, label: this.product.pro_vend_nome || `#${current}` });
            }
            return options;
        },
    },
    created() {
        Promise.all([this.loadCategories(), this.loadStores(), this.loadProviders()])
            .catch((error) => ErrorHandlerService.handle(error, this.$store))
            .finally(() => {
                this.loading = false;
            });
        if (!this.isEdit) this.loadEanSuggestion();
    },
    methods: {
        filterOption(input, option) {
            const normalize = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
            const children = (option.componentOptions && option.componentOptions.children) || [];
            const label = children.map((child) => child.text || "").join("");
            return normalize(label).includes(normalize(input));
        },
        digitsFrom(event) {
            const value = String(event.target.value || "").replace(/\D/g, "");
            event.target.value = value;
            return value;
        },
        onEanInput(event) {
            this.form.ean = this.digitsFrom(event);
        },
        onPackQtyInput(event) {
            this.form.packQty = this.digitsFrom(event);
        },
        onSuggestionInput(event, store) {
            this.$set(store, "qtd_sugestao", this.digitsFrom(event));
        },
        onWrapChange(value) {
            if (value === "UN") this.form.packQty = "1";
            else if (value === "DZ") this.form.packQty = "12";
        },
        toggleGeneric(checked) {
            this.form.isGeneric = checked;
            this.form.wrapCode = undefined;
            this.form.genericUnit = undefined;
            this.form.internalCode = "";
            this.editingGenericUnit = false;
            if (checked) this.form.packQty = "1";
        },
        loadCategories() {
            return this.products_v3_service.categories().then(({ data }) => {
                this.categories = (Array.isArray(data) ? data : data.data || []).map((c) => ({
                    cat_id: c.id,
                    cat_descricao: c.name,
                }));
            });
        },
        loadStores() {
            if (!this.isEdit) {
                return this.store_service.load().then(({ data }) => {
                    this.stores = (Array.isArray(data) ? data : data.data || []).map((store) => ({
                        loj_id: store.id,
                        loj_nomeFantasia: store.trading_name,
                        qtd_sugestao: null,
                    }));
                });
            }
            const fromProduct = Array.isArray(this.product.lojas)
                ? Promise.resolve(this.product.lojas)
                : this.products_v3_service.load(this.product.pro_id).then(({ data }) => data.lojas || []);
            return fromProduct.then((stores) => {
                this.stores = stores.map((store) => ({
                    loj_id: store.loj_id,
                    loj_nomeFantasia: store.loj_nomeFantasia,
                    qtd_sugestao: store.qtd_padrao,
                }));
            });
        },
        loadProviders() {
            return this.provider_service.load_providers({ related: true, noPagination: true }).then(({ data }) => {
                this.providers = data.data || data || [];
                const current = this.product.pro_for_id
                    ? this.providers.find((p) => p.id == this.product.pro_for_id)
                    : this.product.pro_for_nome && this.providers.find((p) => p.name === this.product.pro_for_nome);
                if (current) {
                    this.form.providerId = current.id;
                    this.loadSellers(current.id);
                }
            });
        },
        loadSellers(providerId) {
            this.loadingSellers = true;
            return this.sellers_service
                .load_sellers({ provider_id: providerId })
                .then(({ data }) => {
                    this.sellers = data || [];
                })
                .catch(() => {
                    this.sellers = [];
                })
                .finally(() => {
                    this.loadingSellers = false;
                });
        },
        onProviderChange(value) {
            this.form.providerId = value;
            this.form.sellerId = undefined;
            this.sellers = [];
            if (value) this.loadSellers(value);
        },
        loadEanSuggestion() {
            this.products_v3_service
                .code_suggestion()
                .then(({ data }) => {
                    if (!this.form.ean && data.suggestion != null) this.form.ean = String(data.suggestion);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        validate() {
            const f = this.form;
            this.errors = {
                description: !f.description,
                category: !f.categoryId,
                ean: !this.eanLocked && !f.ean,
                wrap: f.isGeneric ? !f.genericUnit : !f.wrapCode,
                packQty: !f.isGeneric && !f.packQty,
                seller: !!f.providerId && !f.sellerId,
            };
            return !Object.values(this.errors).some(Boolean);
        },
        buildPayload() {
            const f = this.form;
            return {
                pro_descricao: f.description,
                cat_id: f.categoryId,
                pro_ean: f.ean || null,
                pro_codInterno: f.internalCode || null,
                pro_generico: f.isGeneric ? 1 : 0,
                pro_unidade_compra: f.isGeneric ? f.genericUnit : null,
                pro_embalagem: f.isGeneric ? "UN 1" : `${f.wrapCode} ${f.packQty}`,
                pro_pedido_direto: f.directOrder ? 1 : 0,
                pro_status: f.active ? 1 : 0,
                pro_vend_id: f.providerId && f.sellerId ? f.sellerId : null,
                lojas: this.stores.map((store) => ({
                    loj_id: store.loj_id,
                    qtd_padrao: store.qtd_sugestao === "" || store.qtd_sugestao == null ? null : Number(store.qtd_sugestao),
                })),
            };
        },
        submit() {
            this.serverMsg = "";
            if (!this.validate()) {
                this.serverMsg = "Preencha os campos indicados.";
                return;
            }
            if (this.isEdit) this.update();
            else this.create();
        },
        async update() {
            this.saving = true;
            try {
                const { data } = await this.products_v3_service.update(this.product.pro_id, this.buildPayload());
                this.$emit("saved", data);
                if (this.page) {
                    this.dataUnlocked = false;
                    if (Array.isArray(data.lojas)) {
                        this.stores = data.lojas.map((store) => ({
                            loj_id: store.loj_id,
                            loj_nomeFantasia: store.loj_nomeFantasia,
                            qtd_sugestao: store.qtd_padrao,
                        }));
                    }
                } else {
                    this.$emit("close");
                }
                this.$swal.fire("Editado!", "Produto editado com sucesso.", "success");
            } catch (error) {
                const data = (error.response && error.response.data) || {};
                const firstError = data.errors && Object.values(data.errors)[0];
                this.serverMsg = (firstError && firstError[0]) || data.message || "Erro ao editar produto";
            } finally {
                this.saving = false;
            }
        },
        async create() {
            this.saving = true;
            try {
                await this.products_v3_service.create(this.buildPayload());
                this.$emit("saved");
                this.$emit("close");
                this.$swal.fire("Inserido!", "Produto inserido com sucesso.", "success");
            } catch (error) {
                const data = (error.response && error.response.data) || {};
                const firstError = data.errors && Object.values(data.errors)[0];
                this.serverMsg = (firstError && firstError[0]) || data.message || "Erro ao cadastrar produto";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pfm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.pfm-page {
        max-height: none;
        overflow: visible;

        .pfm-body {
            overflow: visible;
            padding: 0;
            border-top: none;
        }

        .pfm-foot {
            padding: 14px 0 0;
        }
    }
}
.pfm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.pfm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.pfm-x.ant-btn,
.pfm-x.ant-btn:focus,
.pfm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.pfm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.pfm-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.pfm-body ::v-deep .ant-form-item {
    margin-bottom: 10px;
}
.pfm-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    margin-bottom: 12px;
    border-radius: 8px;
    font-size: 13px;
    background: #fff1f0;
    border: 1px solid #ffa39e;
    color: #cf1322;

    .material-icons-outlined {
        font-size: 18px;
    }
}
.pfm-inline {
    display: flex;
    gap: 6px;
}
.pfm-icon-btn.ant-btn {
    flex: 0 0 32px;
    width: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .material-icons-outlined {
        font-size: 18px;
    }
}
.pfm-flags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 24px;
    margin: 4px 0 16px;
}
.pfm-section-title {
    font-weight: 600;
    font-size: 14px;
    color: #262626;
    margin-bottom: 8px;
}
.pfm-qty {
    width: 90px;
    text-align: center;
}
.pfm-btn-icon {
    margin-right: 6px;
}
.pfm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.pfm-foot-left {
    margin-right: auto;
}
.pfm-unlock-btn.ant-btn {
    display: flex;
    align-items: center;
    gap: 4px;

    .material-icons-outlined {
        font-size: 18px;
    }
}
</style>

<style lang="scss">
.pfm-wrap {
    .ant-modal {
        min-width: 600px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
